import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { useApiClient } from '@marageti/z4-lib';
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import AlertTitle from '@mui/material/AlertTitle';
import Alert from '@mui/material/Alert';
import TravelerCard from './traveler-card';
import { TravelerData } from './types';
import { validateEmail } from '../../../utils/helpers/validation';

interface MergeTravelersViewProps {
  traveler1Data: TravelerData;
  traveler2Data: TravelerData;
  mergeResults: string[];
  isMerging: boolean;
  mergeTravelers: () => Promise<string[]>;
  setMergeResults: React.Dispatch<React.SetStateAction<string[]>>;
  setIsMerging: React.Dispatch<React.SetStateAction<boolean>>;
  submissionError?: ApiError;
  setSubmissionError: React.Dispatch<React.SetStateAction<ApiError | undefined>>;
  canMerge: boolean;
  email1: string;
  email2: string;
  setEmail1: React.Dispatch<React.SetStateAction<string>>;
  setEmail2: React.Dispatch<React.SetStateAction<string>>;
}

export default function MergeTravelersView({
  traveler1Data,
  traveler2Data,
  mergeResults,
  isMerging,
  mergeTravelers,
  setMergeResults,
  setIsMerging,
  submissionError,
  setSubmissionError,
  canMerge,
  email1,
  email2,
  setEmail1,
  setEmail2,
}: MergeTravelersViewProps) {
  const [inputValueEmail1, setInputValueEmail1] = React.useState(email1);
  const [inputValueEmail2, setInputValueEmail2] = React.useState(email2);
  const apiClient = useApiClient();
  const [mergeModalOpen, setMergeModalOpen] = React.useState(false);

  const [expandedSections, setExpandedSections] = React.useState<{
    tripRequests: boolean;
    tripMatches: boolean;
    reviews: boolean;
    notes: boolean;
  }>({
    tripRequests: false,
    tripMatches: false,
    reviews: false,
    notes: false,
  });

  const handleAccordionChange = (section: keyof typeof expandedSections) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedSections({
        tripRequests: section === 'tripRequests',
        tripMatches: section === 'tripMatches',
        reviews: section === 'reviews',
        notes: section === 'notes',
      });
    } else {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: false,
      }));
    }
  };

  const handleMergeClick = () => {
    setMergeModalOpen(true);
  };

  const handleCloseMergeModal = () => {
    setEmail1('');
    setEmail2('');
    setInputValueEmail1('');
    setInputValueEmail2('');
    setMergeResults([]);
    setSubmissionError(undefined);
    setMergeModalOpen(false);
  };

  const handleInputChangeEmail1 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const validEmail = validateEmail(value);
    setInputValueEmail1(value);
    if (validEmail) {
      setEmail1(value);
    }
  };

  const handleInputChangeEmail2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const validEmail = validateEmail(value);
    setInputValueEmail2(value);
    if (validEmail) {
      setEmail2(value);
    }
  };

  const handleConfirmMerge = async () => {
    setIsMerging(true);
    setSubmissionError(undefined);

    try {
      const results = await mergeTravelers();
      setMergeResults(results);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Merge failed:', error);
      setMergeResults(['Error: Merge operation failed']);
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ my: 6 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h2" gutterBottom>
          Merge Travelers
        </Typography>
        <Typography variant="body1" gutterBottom>
          Merge two traveler accounts to consolidate trip history.
        </Typography>
      </Box>

      {/* Email Inputs */}
      <Box sx={{ mb: 6 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5.5}>
            <TextField
              fullWidth
              label="Old Account Email"
              name="email1"
              value={inputValueEmail1}
              onChange={handleInputChangeEmail1}
              defaultValue={email1}
              placeholder="Enter old account email address"
            />
          </Grid>
          <Grid item xs={12} md={1}>
            {/* Spacer to align with merge icon */}
          </Grid>
          <Grid item xs={12} md={5.5}>
            <TextField
              fullWidth
              label="Primary Account Email"
              name="email2"
              value={inputValueEmail2}
              onChange={handleInputChangeEmail2}
              defaultValue={email2}
              placeholder="Enter primary account email address"
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={1}>
        {/* Old Account Column */}
        <Grid item xs={12} md={5.5}>
          <TravelerCard
            travelerData={traveler1Data}
            title="Old Account"
            placeholderText="Enter the email for the account you want to merge and permanently delete. All trip requests, trip matches, reviews, and notes will be transferred from this account."
            apiClient={apiClient}
            expandedSections={expandedSections}
            onAccordionChange={handleAccordionChange as any}
          />
        </Grid>

        {/* Merge Icon Column */}
        <Grid item xs={12} md={1}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            height: '100%',
            pt: 3,
          }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 1,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 35,
                height: 35,
              }}
            >
              <CallMergeIcon sx={{ fontSize: 16, transform: 'rotate(90deg)' }} />
            </Paper>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                textAlign: 'center',
                color: 'primary.main',
                fontWeight: 'bold',
                fontSize: '0.7rem',
              }}
            >
              Merging into
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mt: 0.25,
                fontSize: '0.65rem',
              }}
            >
              Old → New
            </Typography>
          </Box>
        </Grid>

        {/* Primary Account Column */}
        <Grid item xs={12} md={5.5}>
          <TravelerCard
            travelerData={traveler2Data}
            title="Primary Account"
            placeholderText="Enter the email for the account you want to keep active. It will consolidate all trip requests and related info.
            Personal details such as traveler name, email, phone number, and tags will remain the same."
            apiClient={apiClient}
            expandedSections={expandedSections}
            onAccordionChange={handleAccordionChange as any}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleMergeClick}
          disabled={!canMerge}
          sx={{ px: 4, py: 1.5 }}
        >
          Merge Travelers
        </Button>
      </Box>

      {/* Merge Confirmation Modal */}
      <Dialog
        open={mergeModalOpen}
        onClose={handleCloseMergeModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Confirm Traveler Merge
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The account for
            {' '}
            <strong>
              {traveler1Data.traveler?.givenName}
              {' '}
              {traveler1Data.traveler?.familyName}
              {' '}
              (
              {traveler1Data.traveler?.email}
              )
            </strong>
            {' '}
            will be permanently deleted after the merge is complete. This action cannot be undone.
          </Typography>

          {traveler1Data.traveler && traveler2Data.traveler && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader title="Merge Summary" titleTypographyProps={{ variant: 'h4' }} />
              <CardContent>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                }}
                >
                  <Typography variant="body2">
                    <strong>From:</strong>
                    {' '}
                    {traveler1Data.traveler.givenName}
                    {' '}
                    {traveler1Data.traveler.familyName}
                    {' '}
                    (
                    {traveler1Data.traveler.email}
                    )
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">
                    <strong>To:</strong>
                    {' '}
                    {traveler2Data.traveler.givenName}
                    {' '}
                    {traveler2Data.traveler.familyName}
                    {' '}
                    (
                    {traveler2Data.traveler.email}
                    )
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          <Box>
            <Typography variant="h4" gutterBottom>Merge Results:</Typography>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'paper.background',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
                borderRadius: 2,
                fontFamily: 'monospace',
                maxHeight: 300,
                overflow: 'auto',
                minHeight: 100,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  background: 'transparent',
                },
              }}
            >
              {isMerging && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={16} color="inherit" />
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Merging travelers...
                  </Typography>
                </Box>
              )}
              {!isMerging && mergeResults.length > 0 && (
                mergeResults.map((result) => (
                  <Box key={`merge-result-${result.substring(0, 20)}`} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" component="span" color="text.secondary">
                      {result}
                    </Typography>
                  </Box>
                ))
              )}
              {!isMerging && mergeResults.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Waiting for merge to start...
                </Typography>
              )}
            </Paper>
          </Box>

          {submissionError && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <AlertTitle>There was an error merging travelers.</AlertTitle>
              {submissionError.message}
              <br />
              {submissionError.code && `Code: ${submissionError.code}`}
              <br />
              {submissionError.id && `ID: ${submissionError.id}`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {mergeResults.length === 0 && (
          <Button
            onClick={handleConfirmMerge}
            variant="contained"
            color="primary"
            disabled={isMerging}
          >
            {isMerging ? 'Merging...' : 'Confirm Merge'}
          </Button>
          )}
          <Button
            onClick={handleCloseMergeModal}
            disabled={isMerging}
            variant="outlined"
          >
            {mergeResults.length > 0 ? 'Close' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
